import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Anchor, CloudSun, LayoutDashboard, Map, Navigation as NavigationIcon } from "lucide-react";
import { getRouteKeyFromPath } from "@/lib/i18nRoutes";
import { getShellCopy } from "@/lib/shellTranslations";
import { getMobileStackPath, writeMobileStack } from "@/lib/mobileNavigation";
import { useLanguage } from "@/lib/LanguageContext";
import { getNavLabel } from "@/config/navigation";
import { cn } from "@/lib/utils";

const tabId = (label) => label.toLowerCase().replace(/\s+/g, "_");
const scrollKey = (label) => `marstick_mobile_bottom_nav_scroll_${tabId(label)}`;
const PENDING_SCROLL_RESTORE_KEY = "marstick_mobile_tab_pending_scroll_key";

export const MOBILE_NATIVE_TABS = [
  { key: "dashboard", labelKey: "dashboard", fallbackLabel: "Dashboard", path: "/dashboard", icon: LayoutDashboard, routeKeys: ["dashboard"], itemKeys: ["dashboard"] },
  { key: "weather", labelKey: "weather", fallbackLabel: "Weather", path: "/weather", icon: CloudSun, routeKeys: ["weather", "marine-weather", "weather-forecast"], itemKeys: ["weather", "marineWeather", "weatherForecast"] },
  { key: "navigation", labelKey: "navigation", fallbackLabel: "Navigation", path: "/navigation", icon: NavigationIcon, routeKeys: ["navigation"], itemKeys: ["navigation"] },
  { key: "trips", labelKey: "trips", fallbackLabel: "Trips", path: "/trips", icon: Map, routeKeys: ["trips"], itemKeys: ["trips"] },
  { key: "boats", labelKey: "boats", fallbackLabel: "Boats", path: "/boats", icon: Anchor, routeKeys: ["boats"], itemKeys: ["boats"] },
];

function getTabRootPath(tab) {
  return tab.path;
}

export const mobileBottomNavRootKeys = new Set(MOBILE_NATIVE_TABS.flatMap((tab) => tab.routeKeys));
export const mobileBottomNavPrimaryKeys = new Set(MOBILE_NATIVE_TABS.flatMap((tab) => tab.itemKeys));

function stripLang(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] && parts[0].length === 2) parts.shift();
  return `/${parts.join("/")}`;
}

function matchesTab(pathname, tab) {
  const normalized = stripLang(pathname);
  const routeKey = getRouteKeyFromPath(pathname);
  return tab.itemKeys.includes(routeKey) || tab.routeKeys.some((key) => normalized === `/${key}` || normalized.startsWith(`/${key}/`));
}

export function getMobileNativeTabKey(pathname) {
  return MOBILE_NATIVE_TABS.find((tab) => matchesTab(pathname, tab))?.key || null;
}

export function isMobileBottomNavRoot(pathname) {
  const normalized = stripLang(pathname).replace(/\/+$/, "") || "/";
  if (normalized === "/") return true;
  const routeKey = getRouteKeyFromPath(pathname);
  const singleSegmentRoot = normalized !== "/" && !normalized.slice(1).includes("/");
  return MOBILE_NATIVE_TABS.some((tab) => normalized === tab.path || (singleSegmentRoot && tab.itemKeys.includes(routeKey)));
}

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const activeTab = MOBILE_NATIVE_TABS.find((tab) => matchesTab(location.pathname, tab));
  const shell = getShellCopy(language || "en");
  const getLabel = (tab) => getNavLabel(tab.labelKey, language) || "";

  React.useEffect(() => {
    if (activeTab) {
      writeMobileStack(activeTab.key, `${location.pathname}${location.search || ""}${location.hash || ""}`);
    }
  }, [activeTab?.key, location.pathname, location.search, location.hash]);

  React.useEffect(() => {
    if (!activeTab) return;
    const saveScroll = () => sessionStorage.setItem(scrollKey(activeTab.key), String(window.scrollY || 0));
    window.addEventListener("pagehide", saveScroll);
    window.addEventListener("beforeunload", saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener("pagehide", saveScroll);
      window.removeEventListener("beforeunload", saveScroll);
    };
  }, [activeTab?.key]);

  const handleTabClick = (tab) => {
    const currentPath = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    if (activeTab) {
      sessionStorage.setItem(scrollKey(activeTab.key), String(window.scrollY || 0));
      writeMobileStack(activeTab.key, currentPath);
    }
    const isActive = activeTab?.key === tab.key;
    const rootPath = getTabRootPath(tab);

    if (isActive) {
      writeMobileStack(tab.key, rootPath);
      navigate(rootPath, { replace: true });
      return;
    }

    const savedPath = getMobileStackPath(tab.key);
    if (savedPath) sessionStorage.setItem(PENDING_SCROLL_RESTORE_KEY, scrollKey(tab.key));
    navigate(savedPath || rootPath);
  };

  return (
    <nav
      className="fixed z-[95] select-none rounded-[1.35rem] border border-white/80 bg-white p-1 text-slate-950 shadow-[0_-10px_30px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/80 backdrop-blur-2xl dark:border-white/15 dark:bg-slate-950 dark:text-white dark:ring-white/10 md:hidden"
      style={{ left: "max(0.5rem, env(safe-area-inset-left, 0px))", right: "max(0.5rem, env(safe-area-inset-right, 0px))", bottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}
      aria-label={shell.primaryMobileNavigation || ""}
    >
      <div className="grid min-h-[4.2rem] grid-cols-5 rounded-2xl bg-slate-100 p-1 dark:bg-white/10" role="tablist" aria-label={shell.primaryMobileSections || ""}>
        {MOBILE_NATIVE_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab?.key === tab.key;
          const label = getLabel(tab);
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={label}
              onClick={() => handleTabClick(tab)}
              className={cn("flex cursor-pointer select-none flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 text-center transition-colors duration-200 active:bg-slate-200 focus-visible:ring-2 focus-visible:ring-primary/60 dark:active:bg-white/15", active ? "bg-slate-950 text-white shadow-md dark:bg-white dark:text-slate-950" : "text-slate-700 dark:text-cyan-50")}
            >
              <Icon className="h-5 w-5 shrink-0 select-none" aria-hidden="true" />
              <span className="max-w-full truncate select-none text-[9px] font-black leading-tight opacity-95">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}